import { ReactElement, RefObject, useState } from 'react'
import { I18n, Translate } from 'common'
import { defaultTheme } from '../../../../../src/theme/index'
import { breakpoints } from '../../../../../src/theme/breakpoints'
import styled from 'styled-components'
import { Print } from '../../../../../src/ui-components/util/Print'
import { YourCost } from './YourCost'
import { RECEIPT_PRINT_PAGE_STYLE } from './receipt/receiptPrintPageStyle'
import {
  hasErrors,
  handleBackToCart,
} from '../../../../../src/features/SummaryPanel/CommonFunctions'
import { OrderResponse } from '../../../../../src/api/cart/interfaces'
import { ArrowBack } from '@mui/icons-material'
import { Hyperlink } from '../../../../../src/ui-components/util/Hyperlink'
import { useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'

interface HeaderProps {
  title: string
  orderDate: string
  showYourCost: boolean
  setShowYourCost: (val: boolean) => void
  printRef: RefObject<HTMLDivElement> | null
  orderResponse?: OrderResponse
}

export const Header = (props: HeaderProps): ReactElement => {
  const {
    title,
    orderDate,
    showYourCost,
    setShowYourCost,
    printRef,
    orderResponse,
  } = props

  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const errors = !orderResponse || hasErrors(orderResponse)

  const handleShowYourCost = () => {
    setShowYourCost(!showYourCost)
  }

  const backToCartHandler = async () => {
    setLoading(true)
    try {
      await handleBackToCart(orderResponse, navigate)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Back to cart navigation failed:', error)
      navigate('/cart')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container>
      {errors && (
        <Hyperlink action={backToCartHandler} underlined={false}>
          {loading ? <StyledSpinner animation="border" /> : <StyledArrowBack />}
          <StyledI18n name="backToCart" />
        </Hyperlink>
      )}
      <Section type="primary">
        <OrderDetailsTitle>
          <I18n name={title} />
        </OrderDetailsTitle>
        <YourCost onClick={handleShowYourCost} showYourCost={showYourCost} />
      </Section>
      <WrapOnMobileSection type="secondary">
        <StyledText>
          {/* TODO: date from api */}
          {Translate('orderedOn', [orderDate])}
        </StyledText>
        <Print
          printRef={printRef}
          documentTitle=""
          pageStyle={RECEIPT_PRINT_PAGE_STYLE}
          displayText={Translate('viewOrPrint')}
        />
      </WrapOnMobileSection>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${defaultTheme.colors.white};
  padding: 15px 25px;

  @media print {
    display: none;
  }
`

interface SectionProps {
  type: 'primary' | 'secondary'
  mobile?: boolean
}

const Section = styled.div<SectionProps>`
  display: flex;
  font-size: ${(p) => (p.type === 'primary' ? '24px' : '16px')};
  font-family: ${(p) =>
    p.type === 'primary' ? 'Oswald, serif' : 'Roboto, serif'};
  font-weight: ${(p) => (p.type === 'primary' ? 'bold' : 'none')};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: ${(p) => (p.type === 'primary' ? '10px' : '0px')};

  @media screen and (max-width: ${breakpoints.tablet}px) {
    margin-top: 10px;
    align-items: baseline;
  }
  @media print {
    font-size: 18px;
  }
`

const WrapOnMobileSection = styled(Section)`
  @media screen and (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
  }
`

const OrderDetailsTitle = styled.p`
  font-size: 20px;
`

const StyledText = styled.div`
  text-align: left;
  width: fit-content;

  @media screen and (max-width: ${breakpoints.tablet}px) {
    font-weight: 400;
    font-size: 14px;
  }
`

const StyledSpinner = styled(Spinner)`
  width: 18px;
  height: 18px;
  border-width: medium;
  margin-right: 5px;
`

const StyledArrowBack = styled(ArrowBack)`
  width: 18px;
  margin-right: 5px;
`

const StyledI18n = styled(I18n)`
  font-size: 16px;
`
